// client/src/components/MerchantGestion.js

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { firestore } from '../../firebase';
import {
  doc,
  updateDoc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  setDoc,
  onSnapshot,
  deleteField,
  arrayUnion,
  orderBy,
  limit as qLimit,
  serverTimestamp
} from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import '../CSS/Dashboard.css';
import { useNavigate, useLocation } from 'react-router-dom';
import GestionTabs from './GestionTabs';
import MerchantNavbar from './MerchantNavbar';
import { FaCheckCircle, FaTimesCircle, FaCog, FaTrash, FaWifi, FaShoppingCart, FaGift, FaStar, FaCommentDots, FaSearch } from 'react-icons/fa';
import { sanitize } from '../../utils/sanitize';

const SPECIALS = ['!', '@', '#', '%', '&', '*', '?'];
const LOWER = 'abcdefghijklmnopqrstuvwxyz'.split('').filter(c => c !== 'i' && c !== 'l');
const UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').filter(c => c !== 'I' && c !== 'L');
const DIGITS = '0123456789'.split('');

// ──────────────────────────────────────────────────────────────────────────────
// Helpers précision décimale (centimes)
// ──────────────────────────────────────────────────────────────────────────────
const parseFlexibleNumber = (val) => {
  if (val === null || val === undefined) return 0;
  const s = String(val).replace(',', '.').trim();
  const n = parseFloat(s);
  return isFinite(n) ? n : 0;
};
const toCents = (n) => Math.round(parseFlexibleNumber(n) * 100);
const fromCents = (cents) => parseFloat((Math.round(cents) / 100).toFixed(2));
const formatPts = (n) =>
  parseFlexibleNumber(n).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

// ------ FORMAT DATE (desktop compact / mobile dd/mm/aa) ------
const formatCompactDateFR = (dateObj) => {
  if (!(dateObj instanceof Date) || isNaN(dateObj)) return '—';
  const WD = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const MO = ['jan', 'fev', 'mar', 'avr', 'mai', 'jun', 'jul', 'aou', 'sep', 'oct', 'nov', 'dec'];
  const wd = WD[dateObj.getDay()];
  const d = dateObj.getDate();
  const m = MO[dateObj.getMonth()];
  const y = dateObj.getFullYear();
  const hh = String(dateObj.getHours()).padStart(2, '0');
  const mm = String(dateObj.getMinutes()).padStart(2, '0');
  return `${wd} ${d} ${m} ${y} ${hh}:${mm}`;
};

// ✅ dd/mm/aa sans heure
const formatDDMMYY = (dateObj) => {
  if (!(dateObj instanceof Date) || isNaN(dateObj)) return '—';
  const d = String(dateObj.getDate()).padStart(2, '0');
  const m = String(dateObj.getMonth() + 1).padStart(2, '0');
  const y = String(dateObj.getFullYear()).slice(-2);
  return `${d}/${m}/${y}`;
};

// Construit un code en ordre strict
const buildCode = (digitsCount) => {
  let digits = '';
  for (let i = 0; i < digitsCount; i++) digits += DIGITS[Math.floor(Math.random() * DIGITS.length)];
  const special = SPECIALS[Math.floor(Math.random() * SPECIALS.length)];
  const lower = LOWER[Math.floor(Math.random() * LOWER.length)];
  const upper = UPPER[Math.floor(Math.random() * UPPER.length)];
  return `${digits}${special}${lower}${upper}`;
};

const codeExistsInFidelity = async (code) => {
  const q = query(collection(firestore, 'fidelity'), where('ShortID', '==', code));
  const snap = await getDocs(q);
  return !snap.empty;
};

const generateUniqueShortId = async () => {
  let digitsCount = 3;
  const MAX_ATTEMPTS_PER_LENGTH = 120;
  const MAX_TOTAL_ATTEMPTS = 5000;
  let attemptsAtThisLength = 0;
  let totalAttempts = 0;
  while (totalAttempts < MAX_TOTAL_ATTEMPTS) {
    const candidate = buildCode(digitsCount);
    const exists = await codeExistsInFidelity(candidate);
    if (!exists) return candidate;
    attemptsAtThisLength++; totalAttempts++;
    if (attemptsAtThisLength >= MAX_ATTEMPTS_PER_LENGTH) { digitsCount++; attemptsAtThisLength = 0; }
  }
  throw new Error('Impossible de générer un ShortID unique après de nombreux essais.');
};

const ensureShortIdExists = async (uid) => {
  if (!uid) return;
  const ref = doc(firestore, 'fidelity', uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const data = snap.data() || {};
  if (data.ShortID) return;
  const code = await generateUniqueShortId();
  await setDoc(ref, { ShortID: code }, { merge: true });
};

// ─── Confirme que les writes ont bien été commit côté serveur ──────────────────
const waitForCommit = (refDoc) => new Promise((resolve, reject) => {
  try {
    const unsub = onSnapshot(refDoc, { includeMetadataChanges: true }, (snap) => {
      if (!snap.exists()) return;
      if (!snap.metadata.hasPendingWrites) { unsub(); resolve(); }
    }, (e) => { try { if (unsub) unsub(); } catch {} reject(e); });
  } catch (e) { reject(e); }
});

// ─── Hook utilitaire: détecter mobile ──────────────────────────────────────────
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 768px)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
};

// ✅ 1059px → 771px
const useIsMid = () => {
  const [isMid, setIsMid] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 1059px) and (min-width: 771px)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 1059px) and (min-width: 771px)');
    const handler = (e) => setIsMid(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMid;
};

const MerchantGestion = () => {
  // ─── Auth / User ───────────────────────────────────────────────────────────────
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const auth = getAuth();
  const isMobile = useIsMobile();
  const isMid = useIsMid();

  // Cache des paramètres fidélité
  const [fidSettings, setFidSettings] = useState({
    multiscan: false,
    Type: null,
    gift: '',
    multiple: 5,
    pointsPaliers: []
  });

  useEffect(() => {
    let unsubscribeUser = null;
    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        if (unsubscribeUser) { unsubscribeUser(); unsubscribeUser = null; }
        navigate('/logingeneral');
        return;
      }
      const userId = currentUser.uid;
      if (!userId) return;
      const userDocRef = doc(firestore, 'users', userId);
      if (unsubscribeUser) { unsubscribeUser(); unsubscribeUser = null; }
      unsubscribeUser = onSnapshot(userDocRef, (docSnapshot) => {
        const userData = docSnapshot.data() || {};
        setUser({ ...userData, uid: userId, pseudo: userData?.pseudo });
      });
    });
    return () => { if (unsubscribeUser) unsubscribeUser(); unsubscribeAuth(); };
  }, [navigate, auth]);

  // ─── Fidélité : états ─────────────────────────────────────────────────────────
  const [fidelityHistory, setFidelityHistory] = useState([]);
  const [userPseudos, setUserPseudos] = useState({});
  const [codeInput, setCodeInput] = useState('');
  const [result, setResult] = useState(null);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [isOnline, setIsOnline] = useState(() => (typeof navigator !== 'undefined' ? navigator.onLine : true));

  // eslint-disable-next-line
  const [fidelityType, setFidelityType] = useState(null);
  // eslint-disable-next-line
  const [multiScanEnabled, setMultiScanEnabled] = useState(false);
  const [fidelityIncrement, setFidelityIncrement] = useState(1);
  const [showIncrementModal, setShowIncrementModal] = useState(false);
  const [tempIncrement, setTempIncrement] = useState(1);
  const [pendingCodes, setPendingCodes] = useState([]);
  const [customerFidelityStatus, setCustomerFidelityStatus] = useState(null);
  const [showStripeModal, setShowStripeModal] = useState(false);

  // Fidélité par points
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [amountInput, setAmountInput] = useState('');
  const [customerPoints, setCustomerPoints] = useState(0);
  const [pointsPaliers, setPointsPaliers] = useState([]);
  const [customerDocRef, setCustomerDocRef] = useState(null);

  const [showGainsModal, setShowGainsModal] = useState(false);
  const [currentCustomerGains, setCurrentCustomerGains] = useState([]);
  const [currentCustomerDocRef, setCurrentCustomerDocRef] = useState(null);
  // eslint-disable-next-line
  const [fidelityGift, setFidelityGift] = useState('');

  // 🆕 Sélecteur de caisse (par défaut Total)
  const [selectedCashier, setSelectedCashier] = useState('ALL'); // 'ALL' | <uid>

  // 🆕 Vue Activité / Clients (listing fidélité par client)
  const location = useLocation();
  const [viewMode, setViewMode] = useState(
    location.state?.view === 'clients' ? 'clients' : 'activity'
  ); // 'activity' | 'clients'
  const [clientSearch, setClientSearch] = useState('');
  const [clientSort, setClientSort] = useState({ key: 'label', dir: 'asc' });
  const [showAllClients, setShowAllClients] = useState(false);

  const successTimeoutRef = useRef(null);

  const triggerSuccess = () => {
    setResult('success');
    setShowSuccessAnimation(true);
    if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    successTimeoutRef.current = setTimeout(() => {
      setShowSuccessAnimation(false);
      setResult(null);
      successTimeoutRef.current = null;
    }, 2200);
  };

  // ─── Connexion online/offline ─────────────────────────────────────────────────
  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); };
  }, []);

  useEffect(() => () => {
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }
  }, []);

  // ─── Chargement paramètres fidélité + ShortID ─────────────────────────────────
  useEffect(() => {
    if (!user?.uid) return;
    const fidelityDocRef = doc(firestore, 'fidelity', user.uid);
    (async () => {
      const docSnap = await getDoc(fidelityDocRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        const multiscan = data.multiscan === 'yes';
        const type = data.Type;
        const gift = data.gift || '';
        const multiple = parseInt(data.multiple ?? 5, 10) || 5;

        setMultiScanEnabled(multiscan);
        setFidelityType(type);
        setFidelityGift(gift);

        if (!data.ShortID) { try { await ensureShortIdExists(user.uid); } catch {} }

        if (type === 'Fidbypoints') {
          const levels = data.levels || [
            { points: data.points1, gift: data.gift1 },
            { points: data.points2, gift: data.gift2 },
            { points: data.points3, gift: data.gift3 },
          ].filter(p => p.points > 0);
          setPointsPaliers(levels);
          setFidSettings({ multiscan, Type: type, gift, multiple, pointsPaliers: levels });
        } else {
          setFidSettings({ multiscan, Type: type, gift, multiple, pointsPaliers: [] });
        }
      }
    })();
  }, [user]);

  // ─── Codes récents non validés (30 min) — optimisé côté serveur ───────────────
  useEffect(() => {
    if (!user?.uid) return;
    const listRef = collection(firestore, 'fidelity', user.uid, 'list');
    const thirtyMinAgo = new Date(Date.now() - 30 * 60 * 1000);
    const qRecent = query(
      listRef,
      where('createdAt', '>=', thirtyMinAgo),
      orderBy('createdAt', 'desc'),
      qLimit(100)
    );
    const unsubscribe = onSnapshot(qRecent, (snapshot) => {
      const recentCodes = snapshot.docs
        .map(d => d.data())
        .filter(data => data?.code)
        .map(data => data.code);
      setPendingCodes(recentCodes);
    });
    return () => unsubscribe();
  }, [user]);

  // ─── Historique en temps réel (toutes caisses) ───────────────────────────────
  useEffect(() => {
    if (!user?.uid) return;
    const listCollectionRef = collection(firestore, 'fidelity', user.uid, 'list');
    const unsubscribe = onSnapshot(listCollectionRef, (snapshot) => {
      const rows = snapshot.docs.map(docSnap => {
        const data = docSnap.data() || {};
        const docId = docSnap.id;
        return { ...data, docId, id: docId };
      });
      setFidelityHistory(rows);
    });
    return () => unsubscribe();
  }, [user]);

  // ─── Statut entreprise (Stripe) + fidélité active ────────────────────────────
  useEffect(() => {
    (async () => {
      if (!user?.uid) return;
      const entrepriseRef = collection(firestore, 'entreprises');
      // Firestore rule requires list queries to specify an explicit limit (<= 50)
      const qStripe = query(entrepriseRef, where('userId', '==', user.uid), qLimit(50));
      const stripeSnap = await getDocs(qStripe);
      if (stripeSnap.empty) { setShowStripeModal(true); setCustomerFidelityStatus('inactive'); return; }

      let showStripe = false;
      let fidelityActive = false;
      stripeSnap.forEach((d) => {
        const data = d.data();
        if (data.base !== 'active') showStripe = true;
        if (data.fidelity === 'active') fidelityActive = true;
      });
      setShowStripeModal(showStripe);
      setCustomerFidelityStatus(fidelityActive ? 'active' : 'inactive');
    })();
  }, [user]);

  // ─── Flatten + tri (memo) ────────────────────────────────────────────────────
  const flattenedHistory = useMemo(() => {
    const toDate = (raw) => raw?.toDate ? raw.toDate() : new Date(raw || 0);

    const rows = fidelityHistory.flatMap((item) => {
      const docId = item.docId || item.clientUid || item.id;
      if (!docId) return [];
      const baseRow = {
        customerId: docId,
        docId,
        clientUid: item.clientUid || docId,
        customerLabel: item.clientPseudo || item.customerName || item.nom || '',
      };
      const timestamps = (item.timestamps || []).map((ts, i) => ({
        ...baseRow,
        id: `${docId}-ts-${i}`, date: toDate(ts.date), uid: ts.uid || '', type: 'timestamp', count: ts.count
      }));
      const resetstamps = (item.resetstamp || []).map((rs, i) => ({
        ...baseRow,
        id: `${docId}-rs-${i}`, date: toDate(rs.date), uid: rs.uid || '', type: 'resetstamp', count: rs.count, giftAtTime: rs.giftAtTime
      }));
      const pointshistory = (item.pointshistory || []).map((ph, i) => ({
        ...baseRow,
        id: `${docId}-ph-${i}`, date: toDate(ph.date), uid: ph.uid || '', type: 'pointshistory', points: ph.points, montant: ph.montant, gift: ph.gift
      }));
      return [...timestamps, ...resetstamps, ...pointshistory];
    });

    rows.sort((a, b) => b.date - a.date);
    return rows;
  }, [fidelityHistory]);

  // ─── Map UID → pseudo (batch IN) ─────────────────────────────────────────────
  const loadingPseudosRef = useRef(new Set());

  useEffect(() => {
    const uniqueUids = Array.from(new Set(flattenedHistory.map(r => r.uid))).filter(Boolean);
    const missing = uniqueUids.filter(uid => !(uid in userPseudos) && !loadingPseudosRef.current.has(uid));
    if (missing.length === 0) return;

    missing.forEach(uid => loadingPseudosRef.current.add(uid));

    (async () => {
      try {
        // Lectures individuelles plutôt que batch `where in` : une règle de lecture
        // qui échoue sur un seul UID ne tue plus toute la requête (cashier d'un autre
        // marchand, compte supprimé, etc.).
        const results = {};
        await Promise.all(missing.map(async (uid) => {
          try {
            const ds = await getDoc(doc(firestore, 'users', uid));
            results[uid] = ds.exists() ? (ds.data()?.pseudo || uid) : uid;
          } catch {
            results[uid] = uid;
          }
        }));
        setUserPseudos(prev => ({ ...prev, ...results }));
      } finally {
        missing.forEach(uid => loadingPseudosRef.current.delete(uid));
      }
    })();
  }, [flattenedHistory, userPseudos]);

  // ─── Logique points / gains ──────────────────────────────────────────────────
  const verifyCodeExists = async () => {
    try {
      const currentUser = getAuth().currentUser;
      if (!currentUser) return false;
      const uid = currentUser.uid;
      const listCollectionRef = collection(firestore, 'fidelity', uid, 'list');
      const qCode = query(listCollectionRef, where('code', '==', sanitize(codeInput.trim())), qLimit(1));
      const snapshot = await getDocs(qCode);
      if (!snapshot.empty) {
        const customerDoc = snapshot.docs[0];
        setCustomerDocRef(customerDoc.ref);
        const pts = customerDoc.data().points || 0;
        setCustomerPoints(fromCents(toCents(pts)));
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  const handleOpenGainsModal = async (customerId) => {
    if (!user?.uid || !customerId) {
      setResult('error'); setTimeout(() => setResult(null), 4000); return;
    }
    const listCollectionRef = collection(firestore, 'fidelity', user.uid, 'list');
    const docRef = doc(listCollectionRef, customerId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return;
    const customerData = docSnap.data();
    const currentPoints = fromCents(toCents(customerData.points || 0));
    const paliersAtteints = pointsPaliers.filter(p => currentPoints >= p.points);
    if (paliersAtteints.length > 0) {
      setCurrentCustomerGains(paliersAtteints);
      setCurrentCustomerDocRef(docRef);
      setShowGainsModal(true);
    } else {
      alert("Ce client n'a pas encore de gains à utiliser.");
    }
  };

  const handleUseGain = async (gain) => {
    if (!currentCustomerDocRef || !gain) return;
    const snap = await getDoc(currentCustomerDocRef);
    const currentPoints = fromCents(toCents(snap.data().points || 0));
    const newPointsTotal = fromCents(toCents(currentPoints) - toCents(gain.points));
    await updateDoc(currentCustomerDocRef, {
      points: newPointsTotal,
      pointshistory: arrayUnion({
        date: new Date(),
        uid: user.uid,
        points: fromCents(-toCents(gain.points)),
        gift: gain.gift
      })
    });
    await waitForCommit(currentCustomerDocRef);

    setShowGainsModal(false);
    setCurrentCustomerGains([]);
    setCurrentCustomerDocRef(null);
    triggerSuccess();
  };

  // ─── Validation (scan ou points) ─────────────────────────────────────────────
  const handleOpenIncrementModal = async () => {
    const exists = await verifyCodeExists();
    if (!exists) { setResult('error'); setTimeout(() => setResult(null), 4000); return; }
    if (fidSettings.Type === 'Fidbypoints') {
      setShowPointsModal(true);
    } else if (fidSettings.multiscan) {
      setTempIncrement(fidelityIncrement);
      setShowIncrementModal(true);
    } else {
      handleValider();
    }
  };

  const handleConfirmIncrement = () => {
    setShowIncrementModal(false);
    handleValider(tempIncrement);
    setFidelityIncrement(parseInt(tempIncrement, 10) || 1);
  };

  // ✅ POINTS / ACHATS — 100 centimes = 1 point
  const handleConfirmPoints = async () => {
    const normalizedInput = String(amountInput || '').replace(',', '.').trim();
    const amount = parseFlexibleNumber(normalizedInput);
    if (!amount || amount <= 0 || !customerDocRef) {
      setResult("error"); setTimeout(() => setResult(null), 4000); return;
    }
    if (!navigator.onLine) {
      setResult('offline'); setTimeout(() => setResult(null), 4000);
      return;
    }

    try {
      // S'assurer que createdAt existe (pour les requêtes récentes)
      const existing = await getDoc(customerDocRef);
      if (!existing.exists() || !existing.data()?.createdAt) {
        await setDoc(customerDocRef, { createdAt: serverTimestamp() }, { merge: true });
      }

      const addCents = toCents(amount);
      const currentCents = toCents(customerPoints);
      const newTotalCents = currentCents + addCents;
      const newPointsTotal = fromCents(newTotalCents);

      await setDoc(customerDocRef, { clientUid: customerDocRef.id }, { merge: true });
      await updateDoc(customerDocRef, {
        code: deleteField(),
        points: newPointsTotal,
        pointshistory: arrayUnion({
          date: new Date(),
          uid: user.uid,
          points: fromCents(addCents),
          montant: parseFloat(amount.toFixed(2))
        }),
      });

      await waitForCommit(customerDocRef);

      setCustomerPoints(newPointsTotal);
      setAmountInput("");
      setShowPointsModal(false);

      triggerSuccess();
      setCodeInput("");
    } catch (e) {
      console.error("Erreur handleConfirmPoints :", e);
      setResult("error"); setTimeout(() => setResult(null), 4000);
    }
  };

  const handleValider = async (incrementOverride = null) => {
    if (!navigator.onLine) {
      setResult('offline'); setTimeout(() => setResult(null), 4000);
      return;
    }

    try {
      const currentUser = getAuth().currentUser;
      if (!currentUser) { setResult("error"); setTimeout(() => setResult(null), 4000); return; }
      const uid = currentUser.uid;

      const multiple = parseInt(fidSettings.multiple ?? 5, 10) || 5;
      const giftAtTime = fidSettings.gift || 'Gain';
      const increment = parseInt(incrementOverride !== null ? incrementOverride : fidelityIncrement, 10) || 1;

      const listCollectionRef = collection(firestore, "fidelity", uid, "list");
      const qCode = query(
        listCollectionRef,
        where("code", "==", sanitize(codeInput.trim())),
        qLimit(5)
      );
      const querySnapshot = await getDocs(qCode);
      if (querySnapshot.empty) { setResult("error"); setTimeout(() => setResult(null), 4000); return; }

      for (const docSnap of querySnapshot.docs) {
        const docRefLine = docSnap.ref;
        const docData = docSnap.data() || {};
        const currentCompteur = docData.compteur || 0;
        const total = currentCompteur + increment;

        // S'assurer que createdAt est présent une fois pour toutes
        if (!docData.createdAt) {
          await setDoc(docRefLine, { createdAt: serverTimestamp() }, { merge: true });
        }
        await setDoc(docRefLine, { clientUid: docRefLine.id }, { merge: true });

        if (fidSettings.Type === 'Fidbypoints') {
          // normalement on passe par la modale points
        } else {
          if (total < multiple) {
            await updateDoc(docRefLine, {
              code: deleteField(),
              compteur: total,
              timestamps: arrayUnion({ date: new Date(), uid, count: increment }),
            });
          } else {
            await updateDoc(docRefLine, {
              code: deleteField(),
              compteur: total - multiple,
              resetstamp: arrayUnion({ date: new Date(), uid, count: increment, giftAtTime }),
            });
          }
        }

        await waitForCommit(docRefLine);
      }

      setCodeInput("");
      setFidelityIncrement(1);
      triggerSuccess();
    } catch (e) {
      console.error("Erreur handleValider :", e);
      setResult("error"); setTimeout(() => setResult(null), 4000);
    }
  };

  const handleDeleteLastTransaction = async (row) => {
    if (!window.confirm("Êtes-vous certain de vouloir supprimer la dernière transaction ?")) return;
    if (!user?.uid) return;
    try {
      const uid = user.uid;
      const docId = row.docId || row.customerId || row.clientUid || row.id.split(/-(?:ts|rs|ph)-/)[0];
      const docRef = doc(firestore, 'fidelity', uid, 'list', docId);
      const docSnap = await getDoc(docRef);
      const data = docSnap.data() || {};

      const type = fidSettings.Type || 'Fidbycount';

      if (type === 'Fidbypoints') {
        const arr = data.pointshistory || [];
        if (arr.length === 0) return;
        const lastEntry = arr[arr.length - 1];
        const currentCents = toCents(data.points || 0);
        const lastCents = toCents(lastEntry.points || 0);
        const newPoints = fromCents(currentCents - lastCents);
        await updateDoc(docRef, { points: newPoints, pointshistory: arr.slice(0, -1) });
      } else {
        const key = row.type === 'timestamp' ? 'timestamps' : 'resetstamp';
        const arr = data[key] || [];
        if (arr.length === 0) return;
        const multiple = parseInt(fidSettings.multiple ?? 5, 10) || 5;
        const lastEntry = arr[arr.length - 1];
        const decrement = lastEntry.count || 1;
        const currentCompteur = data.compteur || 0;
        const newCompteur = key === 'resetstamp' ? currentCompteur + multiple - decrement : currentCompteur - decrement;
        await updateDoc(docRef, { compteur: newCompteur, [key]: arr.slice(0, -1) });
      }
    } catch (e) {
      console.error('Erreur suppression transaction', e);
    }
  };

  const handleObtenirFidelite = async () => {
    if (!user?.uid) return;
    try {
      const fidelityDocRef = doc(firestore, "fidelity", user.uid);
      const snap = await getDoc(fidelityDocRef);
      if (!snap.exists()) {
        await setDoc(fidelityDocRef, { gift: "5% de remise", multiple: "3", pseudo: user.pseudo, Type: "Fidbycount" });
      } else {
        await setDoc(fidelityDocRef, { gift: "5% de remise", multiple: "3", pseudo: user.pseudo, Type: "Fidbycount" }, { merge: true });
      }
      try { await ensureShortIdExists(user.uid); } catch {}
      const entrepriseRef = collection(firestore, "entreprises");
      const qEnt = query(entrepriseRef, where("userId", "==", user.uid), qLimit(10));
      const s = await getDocs(qEnt);
      if (!s.empty) {
        const updates = s.docs.map(d => updateDoc(doc(firestore, "entreprises", d.id), { fidelity: "active" }));
        await Promise.allSettled(updates);
      }
      navigate("/MerchantFidGestion");
    } catch (e) {
      console.error("Erreur MAJ fidélité", e);
    }
  };

  // 🆕 Options caisses
  const cashierOptions = useMemo(() => {
    const uids = Array.from(new Set(flattenedHistory.map(r => r.uid))).filter(Boolean);
    const options = uids
      .map(uid => ({ uid, label: sanitize(userPseudos[uid] || uid) }))
      .sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }));
    return [{ uid: 'ALL', label: 'Total' }, ...options];
  }, [flattenedHistory, userPseudos]);

  // 🆕 Filtre par caisse
  const filteredHistory = useMemo(() => {
    if (selectedCashier === 'ALL') return [...flattenedHistory];
    return flattenedHistory.filter(r => r.uid === selectedCashier);
  }, [flattenedHistory, selectedCashier]);

  const [feedbacks, setFeedbacks] = useState([]);
  const [commentModalData, setCommentModalData] = useState(null);

  // Notes côté Satisfaction (toutes caisses)
  useEffect(() => {
    if (!user?.uid) return;
    const fbRef = collection(firestore, 'Satisfaction', user.uid, 'feedbacks');
    const qFb = query(fbRef, orderBy('createdAt', 'desc'), qLimit(1000));
    const unsubscribe = onSnapshot(qFb, (snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setFeedbacks(arr);
    });
    return () => unsubscribe();
  }, [user]);

  const findFeedbackForRow = (row) => {
    if (!row || !row.date) return null;
    const txMs = row.date instanceof Date ? row.date.getTime() : null;
    if (!txMs) return null;
    let bestFeedback = null;
    let bestDiff = Infinity;
    feedbacks.forEach((fb) => {
      if (!fb || !fb.uid) return;
      if (fb.uid !== row.clientUid && fb.uid !== row.customerId && fb.uid !== row.docId) return;
      const txDateMs = typeof fb.txDateMillis === 'number'
        ? fb.txDateMillis
        : (typeof fb.createdAt?.toMillis === 'function'
          ? fb.createdAt.toMillis()
          : (fb.createdAt instanceof Date ? fb.createdAt.getTime() : null));
      if (!txDateMs) return;
      const diff = Math.abs(txDateMs - txMs);
      if (diff <= 120000 && diff < bestDiff) { // tolérance 2 min
        bestDiff = diff;
        bestFeedback = fb;
      }
    });
    return bestFeedback;
  };

  const handleOpenCommentModal = (feedback) => {
    if (!feedback || typeof feedback.comment !== 'string') return;
    const cleanedComment = sanitize(feedback.comment).trim();
    if (!cleanedComment) return;

    let fbDate = null;
    if (typeof feedback.txDateMillis === 'number') {
      fbDate = new Date(feedback.txDateMillis);
    } else if (typeof feedback.createdAt?.toDate === 'function') {
      try { fbDate = feedback.createdAt.toDate(); } catch { fbDate = null; }
    } else if (feedback.createdAt instanceof Date) {
      fbDate = feedback.createdAt;
    }

    setCommentModalData({
      comment: cleanedComment,
      rating: Number(feedback.rating) || 0,
      pseudo: sanitize(feedback.pseudo || 'Anonyme'),
      createdAt: fbDate
    });
  };

  const handleCloseCommentModal = () => setCommentModalData(null);

  const handleRowNavigate = (customerId) => {
    if (!customerId) return;
    navigate(`/merchantanalysetransaction/${encodeURIComponent(customerId)}`);
  };

  const handleRowKeyDown = (event, customerId) => {
    if (!customerId) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleRowNavigate(customerId);
    }
  };

  // 🆕 Stats par caisse
  const stats = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const day = (now.getDay() + 6) % 7; // lundi = 0
    const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - day);
    startOfWeek.setHours(0, 0, 0, 0);

    let scansToday = 0, scansWeek = 0, scansTotal = 0;
    let ptsToday = 0, ptsWeek = 0, ptsTotal = 0;

    for (const row of filteredHistory) {
      const d = row.date instanceof Date ? row.date : new Date(row.date);
      if (row.type === 'timestamp' || row.type === 'resetstamp') {
        const count = parseInt(row.count || 1, 10) || 1;
        scansTotal += count;
        if (d >= startOfToday) scansToday += count;
        if (d >= startOfWeek) scansWeek += count;
      }
      if (row.type === 'pointshistory' && parseFlexibleNumber(row.points) > 0) {
        const pts = parseFlexibleNumber(row.points);
        ptsTotal += pts;
        if (d >= startOfToday) ptsToday += pts;
        if (d >= startOfWeek) ptsWeek += pts;
      }
    }
    return { scansToday, scansWeek, scansTotal, ptsToday, ptsWeek, ptsTotal };
  }, [filteredHistory]);

  const suffix = (fidSettings.Type === 'Fidbypoints') ? 'pts' : ' scans';
  const isPoints = fidSettings.Type === 'Fidbypoints';

  // 🆕 Agrégation par client (un client = un doc de fidelity/{uid}/list)
  const clientsAggregated = useMemo(() => {
    const toDate = (raw) => (raw?.toDate ? raw.toDate() : new Date(raw || 0));
    return fidelityHistory
      .map((item) => {
        const docId = item.docId || item.clientUid || item.id;
        if (!docId) return null;
        const timestamps = item.timestamps || [];
        const resetstamp = item.resetstamp || [];
        const pointshistory = item.pointshistory || [];

        let lastVisit = null;
        const considerDate = (raw) => {
          const dt = toDate(raw);
          if (!isNaN(dt) && (!lastVisit || dt > lastVisit)) lastVisit = dt;
        };

        // Passages = nombre de scans cumulés (timestamps + gains validés)
        let passages = 0;
        timestamps.forEach((ts) => { passages += parseInt(ts.count || 1, 10) || 1; considerDate(ts.date); });
        resetstamp.forEach((rs) => { passages += parseInt(rs.count || 1, 10) || 1; considerDate(rs.date); });

        // Points = nombre d'achats + total dépensé
        let purchases = 0;
        let totalSpent = 0;
        let giftsFromPoints = 0;
        pointshistory.forEach((ph) => {
          const pts = parseFlexibleNumber(ph.points);
          if (pts > 0) { purchases += 1; totalSpent += parseFlexibleNumber(ph.montant); }
          else if (pts < 0) { giftsFromPoints += 1; }
          considerDate(ph.date);
        });

        const giftsUsed = resetstamp.length + giftsFromPoints;
        const activityCount = timestamps.length + resetstamp.length + pointshistory.length;

        return {
          docId,
          clientUid: item.clientUid || docId,
          label: item.clientPseudo || item.customerName || item.nom || item.pseudo || '',
          passages,
          purchases,
          totalSpent,
          giftsUsed,
          points: parseFlexibleNumber(item.points),
          compteur: parseInt(item.compteur, 10) || 0,
          lastVisit,
          activityCount,
        };
      })
      .filter((c) => c && c.activityCount > 0);
  }, [fidelityHistory]);

  // 🆕 Résolution des pseudos clients (réutilise le cache userPseudos / loadingPseudosRef)
  useEffect(() => {
    if (viewMode !== 'clients') return;
    const uids = Array.from(new Set(clientsAggregated.map((c) => c.clientUid))).filter(Boolean);
    const missing = uids.filter((uid) => !(uid in userPseudos) && !loadingPseudosRef.current.has(uid));
    if (missing.length === 0) return;
    missing.forEach((uid) => loadingPseudosRef.current.add(uid));
    (async () => {
      try {
        const results = {};
        await Promise.all(missing.map(async (uid) => {
          try {
            const ds = await getDoc(doc(firestore, 'users', uid));
            results[uid] = ds.exists() ? (ds.data()?.pseudo || uid) : uid;
          } catch {
            results[uid] = uid;
          }
        }));
        setUserPseudos((prev) => ({ ...prev, ...results }));
      } finally {
        missing.forEach((uid) => loadingPseudosRef.current.delete(uid));
      }
    })();
  }, [viewMode, clientsAggregated, userPseudos]);

  // 🆕 Liste clients filtrée + triée
  const clientsView = useMemo(() => {
    const term = clientSearch.trim().toLowerCase();
    let arr = clientsAggregated.map((c) => {
      const resolvedName = c.label || userPseudos[c.clientUid] || '';
      // Le résolveur renvoie l'UID en repli quand le client n'a pas de pseudo.
      const isUid = resolvedName === c.clientUid || resolvedName === c.docId;
      const displayName = (resolvedName && !isUid) ? resolvedName : `Client ${String(c.docId).slice(0, 6)}`;
      // Un compte est "connu" seulement s'il a un vrai pseudo, différent de l'UID
      // ET différent du pseudo de repli "Inconnu" → ces comptes sont relégués en bas.
      const hasName = !!resolvedName && !isUid && resolvedName.trim().toLowerCase() !== 'inconnu';
      return {
        ...c,
        hasName,
        displayName,
        loyalty: isPoints ? c.purchases : c.passages,
      };
    });
    if (term) {
      arr = arr.filter((c) =>
        c.displayName.toLowerCase().includes(term) || String(c.docId).toLowerCase().includes(term)
      );
    }
    const { key, dir } = clientSort;
    arr.sort((a, b) => {
      if (key === 'label') {
        // Clients avec pseudo toujours en haut, "inconnus" en bas
        if (a.hasName !== b.hasName) return a.hasName ? -1 : 1;
        const cmp = a.displayName.localeCompare(b.displayName, 'fr', { sensitivity: 'base' });
        return dir === 'asc' ? cmp : -cmp;
      }
      let av; let bv;
      if (key === 'lastVisit') { av = a.lastVisit ? a.lastVisit.getTime() : 0; bv = b.lastVisit ? b.lastVisit.getTime() : 0; }
      else if (key === 'spent') { av = a.totalSpent; bv = b.totalSpent; }
      else if (key === 'points') { av = a.points; bv = b.points; }
      else if (key === 'gifts') { av = a.giftsUsed; bv = b.giftsUsed; }
      else { av = a.loyalty; bv = b.loyalty; }
      return dir === 'asc' ? av - bv : bv - av;
    });
    return arr;
  }, [clientsAggregated, clientSearch, clientSort, isPoints, userPseudos]);

  const toggleClientSort = (key) => {
    setClientSort((prev) => (prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: key === 'label' ? 'asc' : 'desc' }));
  };
  const clientSortArrow = (key) => (clientSort.key === key ? (clientSort.dir === 'asc' ? ' ▲' : ' ▼') : '');

  // ─── Rendu ────────────────────────────────────────────────────────────────────
  return (
    <div>
      <MerchantNavbar />
      {showSuccessAnimation && (
        <div className="success-overlay">
          <div className="success-card">
            <div className="success-check">
              <FaCheckCircle />
            </div>
            <h4 style={{ margin: 0 }}>Fidélité validée</h4>
          </div>
        </div>
      )}
      <div className="dashboard-container">
        <style>{`
  .mg-scope { width: 100%; min-width: 0; }
  .mg-scope .mg-header { align-items: center; }
  .mg-scope .mg-header .eb-page-title { margin: 0; text-align: left; }

  /* Onglets de la section Gestion */
  .mg-scope .tabs-pill { display: inline-flex; gap: 4px; padding: 4px; margin: 0 0 24px; background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius); box-shadow: var(--eb-shadow-xs); max-width: 100%; overflow-x: auto; scrollbar-width: none; }
  .mg-scope .tabs-pill::-webkit-scrollbar { display: none; }
  .mg-scope .tab { appearance: none; border: 0; background: transparent; color: var(--eb-text-2); font-weight: 600; font-size: 14px; min-height: 38px; padding: 0 16px; border-radius: var(--eb-radius-sm); cursor: pointer; white-space: nowrap; transition: background-color .15s ease, color .15s ease; }
  .mg-scope .tab:hover { background: var(--eb-surface-2); color: var(--eb-text); }
  .mg-scope .tab-active, .mg-scope .tab-active:hover { background: var(--eb-brand); color: #fff; cursor: default; box-shadow: var(--eb-shadow-xs); }

  .mg-scope .card { background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); padding: 20px; box-shadow: var(--eb-shadow-sm); }

  /* Statistiques */
  .mg-scope .stats-wrap { margin: 0 0 20px; }
  .mg-scope .stats-header { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; margin-bottom: 12px; }
  .mg-scope .stats-header .eb-toolbar__title { font-size: 16px; text-align: left; }
  .mg-scope .stats-selector { display: flex; align-items: center; gap: 8px; }
  .mg-scope .select-caisse { min-height: 40px; padding: 0 12px; font-size: 14px; font-weight: 600; color: var(--eb-text); }
  .mg-scope .stats-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }
  .mg-scope .stat-card { display: flex; flex-direction: column; justify-content: center; gap: 6px; min-width: 0; height: 100%; }
  .mg-scope .stat-title { font-weight: 500; color: var(--eb-muted); font-size: 13.5px; }
  .mg-scope .stat-value { font-size: 26px; font-weight: 700; letter-spacing: -0.02em; color: var(--eb-text); font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }

  /* Validation d'un code client */
  .mg-scope .mg-validate { margin-bottom: 24px; }
  .mg-scope .mg-validate-title { margin: 0; font-size: 16px; font-weight: 650; color: var(--eb-text); text-align: left; }
  .mg-scope .mg-validate-sub { margin: 2px 0 0; font-size: 13.5px; color: var(--eb-muted); }
  .mg-scope .mg-validate .eb-inline-form__controls { flex-wrap: wrap; }
  .mg-scope .mg-validate .eb-inline-form__controls .eb-input { flex: 1 1 200px; min-height: 42px; }
  .mg-scope .mg-chips { display: flex; flex-wrap: wrap; gap: 8px; flex: 1 1 100%; }
  .mg-scope .mg-chip { font-family: var(--eb-font-mono); }
  .mg-scope .mg-status { display: inline-flex; align-items: center; gap: 6px; font-size: 14px; font-weight: 600; }

  .mg-scope .input-ecb { flex: 1 1 240px; min-height: 42px; padding: 0 14px; font-size: 15px; }

  /* Tableaux */
  .mg-scope .table-fidelity-wrap { width: 100%; margin: 0; padding: 0; overflow-x: auto; }
  .mg-scope .table-fidelity { width: 100%; border-collapse: separate; border-spacing: 0; background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); overflow: hidden; box-shadow: var(--eb-shadow-sm); font-size: 14px; }
  .mg-scope .table-fidelity thead th { background: var(--eb-surface-2); color: var(--eb-muted); font-weight: 600; font-size: 12.5px; text-transform: uppercase; letter-spacing: .04em; border-bottom: 1px solid var(--eb-border); white-space: nowrap; user-select: none; }
  .mg-scope .table-fidelity th, .mg-scope .table-fidelity td { padding: 12px 16px; text-align: left; vertical-align: middle; word-break: break-word; }
  .mg-scope .table-fidelity tbody td { border-bottom: 1px solid var(--eb-border); color: var(--eb-text-2); }
  .mg-scope .table-fidelity tbody tr:last-child td { border-bottom: 0; }
  .mg-scope .table-fidelity tbody tr { transition: background-color .15s ease; }
  .mg-scope .table-fidelity tbody tr:hover { background: var(--eb-brand-50); }
  .mg-scope .table-fidelity tbody tr:focus-visible { outline: 2px solid var(--eb-brand-500); outline-offset: -2px; }
  .mg-scope .table-fidelity .td-date { color: var(--eb-muted); font-size: 13.5px; white-space: nowrap; }
  .mg-scope .table-fidelity .td-type { font-weight: 600; color: var(--eb-text); }
  .mg-scope .mg-table-toolbar { display: flex; flex-direction: column; align-items: stretch; gap: 8px; margin-bottom: 12px; }
  .mg-scope .mg-table-toolbar .eb-input { min-height: 44px; }
  .mg-scope .mg-table-toolbar .mg-count { align-self: flex-end; }
  .mg-scope .mg-count { color: var(--eb-muted); font-weight: 600; font-size: 13.5px; }

  .mg-scope .pill { display: inline-flex; align-items: center; gap: 6px; font-weight: 600; padding: 3px 10px; border-radius: var(--eb-radius-pill); border: 1px solid transparent; font-size: 13px; white-space: nowrap; font-variant-numeric: tabular-nums; }
  .mg-scope .pill-scan { background: var(--eb-brand-50); color: var(--eb-brand); border-color: var(--eb-brand-100); }
  .mg-scope .pill-gift { background: var(--eb-warning-bg); color: var(--eb-warning); border-color: #fde3b0; }
  .mg-scope .pill-pos  { background: var(--eb-success-bg); color: var(--eb-success); border-color: #c6f0da; }
  .mg-scope .pill-neg  { background: var(--eb-danger-bg); color: var(--eb-danger); border-color: #fbd0cb; }
  .mg-scope .comment-badge { width: 30px; height: 30px; border-radius: var(--eb-radius-pill); border: 1px solid var(--eb-brand-100); background: var(--eb-brand-50); color: var(--eb-brand); display: inline-flex; align-items: center; justify-content: center; cursor: pointer; padding: 0; transition: background-color .12s ease; }
  .mg-scope .comment-badge:hover { background: var(--eb-brand-100); }

  .mg-scope .row-gift { background: #fffcf2; }
  .mg-scope .row-in { background: var(--eb-success-bg); }
  .mg-scope .row-out { background: var(--eb-danger-bg); }

  .mg-scope .mg-empty { margin: 0; padding: 32px 16px; text-align: center; color: var(--eb-muted); font-size: 14px; }
  .mg-scope .mg-more { display: flex; justify-content: center; margin-top: 16px; }
  .mg-scope .mg-icon-btn { display: inline-flex; align-items: center; justify-content: center; width: 34px; height: 34px; border-radius: var(--eb-radius-sm); border: 1px solid transparent; background: transparent; color: var(--eb-danger); cursor: pointer; }
  .mg-scope .mg-icon-btn:hover { background: var(--eb-danger-bg); border-color: #fbd0cb; }

  /* Modales */
  .modal-overlay.mg-modal { position: fixed; inset: 0; background: rgba(15, 35, 56, .55); backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; padding: 16px; z-index: 9999; }
  .mg-modal .modal-card { background: var(--eb-surface); padding: 24px; border-radius: var(--eb-radius-xl); width: 100%; max-width: 400px; max-height: calc(100dvh - 32px); overflow-y: auto; box-shadow: var(--eb-shadow-lg); text-align: left; }
  .mg-modal .modal-card h3 { margin: 0 0 12px; text-align: left; font-size: 18px; font-weight: 700; color: var(--eb-text); }
  .mg-modal .mg-modal-actions { display: flex; justify-content: center; gap: 10px; margin-top: 20px; flex-wrap: wrap; }
  .mg-modal .mg-modal-list { display: flex; flex-direction: column; gap: 8px; }

  @media (max-width: 768px) {
    .mg-scope .tabs-pill { display: flex; width: 100%; margin-bottom: 20px; }
    .mg-scope .tab { flex: 1 0 auto; padding: 0 12px; min-height: 40px; }
    .mg-scope .stats-grid { gap: 8px; }
    .mg-scope .card { padding: 14px; }
    .mg-scope .stat-title { font-size: 13px; }
    .mg-scope .stat-card { align-items: center; text-align: center; }
    .mg-scope .stat-value { font-size: 18px; }
    .mg-scope .input-ecb { flex: 1 1 140px; min-width: 0; }
    .mg-scope .mg-validate .eb-inline-form__controls .eb-btn { flex: 1 1 auto; }
    .mg-scope .stats-header .select-caisse { flex: 1 1 auto; min-width: 0; max-width: 100%; }
    .mg-scope .table-fidelity { table-layout: fixed; font-size: 13px; }
    .mg-scope .table-fidelity th, .mg-scope .table-fidelity td { padding: 10px 8px; }
    .mg-scope .table-fidelity thead th { font-size: 11px; white-space: normal; }
    .mg-scope .pill { padding: 3px 8px; font-size: 12.5px; }
    .mg-scope .td-date { font-size: 13px; }
    .mg-modal .mg-modal-actions { flex-direction: column-reverse; }
    .mg-modal .mg-modal-actions > * { width: 100%; min-height: 42px; }
  }
`}</style>

        <div className="mg-scope">
          <header className="eb-page-header mg-header">
            <div>
              <h1 className="eb-page-title">Gestion</h1>
              <p className="eb-page-subtitle">Fidélité, clients, équipe et promotions</p>
            </div>
            {!showStripeModal && customerFidelityStatus === 'active' && (
              <button
                type="button"
                className="settings-button eb-btn eb-btn--secondary"
                onClick={() => navigate('/MerchantFidGestion')}
                title="Paramètres de votre fidélité"
                aria-label="Paramètres"
              >
                <FaCog size={16} aria-hidden="true" />
                Paramètres fidélité
              </button>
            )}
          </header>

        {/* Onglets */}
        <GestionTabs active={viewMode} onSelectView={setViewMode} />

        {showStripeModal && (
          <div className="secret-key-section card">
            <h2 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 700, textAlign: 'left' }}>Activez votre compte entreprise</h2>
            <ol style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 12, color: 'var(--eb-text-2)' }}>
              <li>
                <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 12px' }}>
                  Activez votre compte :
                  <button className="button-classic" onClick={() => {
                    const stripeUrl = process.env.NODE_ENV === 'production'
                      ? 'https://buy.stripe.com/fZeg04cUh6Z6b3W144'
                      : 'https://buy.stripe.com/test_00gaI28o69U1adidQQ';
                    window.open(stripeUrl, '_blank');
                  }}>Activer mon compte</button>
                </span>
              </li>
              <li>Activation sous 24-48h après votre paiement</li>
              <li>Profitez pleinement de votre compte entreprise !</li>
            </ol>
          </div>
        )}

        {!showStripeModal && (
          <>
            {/* État "chargement" ou "inactive" issus de l'exemple */}
            {customerFidelityStatus === null && (
              <div className="card" style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                <span className="eb-spinner" role="status" aria-label="Chargement" />
              </div>
            )}

            {customerFidelityStatus === 'inactive' && (
              <>
                <div className="after-tabs" />
                <div className="card" style={{ textAlign: 'center', padding: '40px 24px' }}>
                  <p className="eb-muted" style={{ margin: '0 0 16px' }}>Le programme de fidélité n'est pas encore activé.</p>
                  <button className="eb-btn eb-btn--primary" onClick={handleObtenirFidelite}>
                    Activer ma fidélité
                  </button>
                </div>
              </>
            )}

            {customerFidelityStatus === 'active' && (
              <>
                <div className="after-tabs" />

                {/* Sélecteur de caisse + 3 cartes (vue Activité uniquement) */}
                {viewMode === 'activity' && (
                <>
                <div className="stats-wrap">
                  <div className="stats-header">
                    <h2 className="eb-toolbar__title">Statistiques</h2>
                    <div className="stats-selector">
                      <select
                        id="select-caisse"
                        className="select-caisse"
                        value={selectedCashier}
                        onChange={(e) => setSelectedCashier(e.target.value)}
                      >
                        {cashierOptions.map(opt => (
                          <option key={opt.uid} value={opt.uid}>
                            {opt.uid === user?.uid ? 'Vous' : opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="stats-grid">
                    <div className="card stat-card">
                      <div className="stat-title">Aujourd&apos;hui</div>
                      <div className="stat-value">
                        {fidSettings.Type === 'Fidbypoints'
                          ? `${formatPts(stats.ptsToday)}${suffix}`
                          : `${stats.scansToday}${suffix}`}
                      </div>
                    </div>
                    <div className="card stat-card">
                      <div className="stat-title">Cette semaine</div>
                      <div className="stat-value">
                        {fidSettings.Type === 'Fidbypoints'
                          ? `${formatPts(stats.ptsWeek)}${suffix}`
                          : `${stats.scansWeek}${suffix}`}
                      </div>
                    </div>
                    <div className="card stat-card">
                      <div className="stat-title">Total</div>
                      <div className="stat-value">
                        {fidSettings.Type === 'Fidbypoints'
                          ? `${formatPts(stats.ptsTotal)}${suffix}`
                          : `${stats.scansTotal}${suffix}`}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="eb-inline-form mg-validate">
                <div className="eb-inline-form__label">
                  <h2 className="mg-validate-title">Valider un passage</h2>
                  <p className="mg-validate-sub eb-muted">Saisissez le code affiché par le client.</p>
                </div>

                {/* Barre de validation */}
                <div className="eb-inline-form__controls">
                  <input
                    type="text"
                    placeholder="Entrez le code client"
                    value={codeInput}
                    onChange={(e) => setCodeInput(sanitize(e.target.value))}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleOpenIncrementModal(); }}
                    className="eb-input"
                  />
                  <button onClick={handleOpenIncrementModal} className="eb-btn eb-btn--primary" disabled={!isOnline} title={!isOnline ? 'Pas de connexion internet' : 'Valider'}>
                    Valider
                  </button>
                  {!isOnline && <FaWifi style={{ color: 'var(--eb-warning)', fontSize: '1.2rem' }} title="Pas de connexion" />}
                  {result === 'success' && <span className="mg-status" style={{ color: 'var(--eb-success)' }}><FaCheckCircle /> Validé</span>}
                  {result === 'offline' && <span className="mg-status" style={{ color: 'var(--eb-warning)' }}><FaTimesCircle /> Pas de connexion</span>}
                  {result === 'error' && <span className="mg-status" style={{ color: 'var(--eb-danger)' }}><FaTimesCircle /> Code invalide</span>}
                </div>

                {/* ✅ Codes en attente (chips cliquables) */}
                {pendingCodes.length > 0 && (
                  <div className="mg-chips">
                      {pendingCodes.map((code, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCodeInput(sanitize(code))}
                          className="eb-btn eb-btn--secondary eb-btn--sm mg-chip"
                        >
                          {sanitize(code)}
                        </button>
                      ))}
                  </div>
                )}
                </div>
                </>
                )}


                {/* 🆕 Vue Clients : un client par ligne + stats de fidélité */}
                {viewMode === 'clients' && (
                  <div className="table-fidelity-wrap">
                    <div className="mg-table-toolbar">
                      <div className="eb-search">
                        <FaSearch className="eb-search__icon" aria-hidden="true" />
                        <input
                          type="text"
                          placeholder="Rechercher un client…"
                          value={clientSearch}
                          onChange={(e) => setClientSearch(sanitize(e.target.value))}
                          className="eb-input"
                          aria-label="Rechercher un client"
                        />
                      </div>
                      <span className="mg-count">
                        {clientsView.length} client{clientsView.length > 1 ? 's' : ''}
                      </span>
                    </div>

                    {clientsView.length > 0 ? (
                      <table className="table-fidelity">
                        <thead>
                          <tr>
                            <th onClick={() => toggleClientSort('label')} style={{ cursor: 'pointer' }}>Client{clientSortArrow('label')}</th>
                            <th onClick={() => toggleClientSort('loyalty')} style={{ cursor: 'pointer' }}>
                              {isPoints ? 'Achats' : 'Passages'}{clientSortArrow('loyalty')}
                            </th>
                            {!(isMobile || isMid) && isPoints && (
                              <th onClick={() => toggleClientSort('spent')} style={{ cursor: 'pointer' }}>Dépensé{clientSortArrow('spent')}</th>
                            )}
                            {!(isMobile || isMid) && (
                              <th
                                onClick={() => toggleClientSort(isPoints ? 'points' : 'gifts')}
                                style={{ cursor: 'pointer' }}
                              >
                                {isPoints ? 'Points' : 'Gains'}{clientSortArrow(isPoints ? 'points' : 'gifts')}
                              </th>
                            )}
                            <th onClick={() => toggleClientSort('lastVisit')} style={{ cursor: 'pointer' }}>Dernière visite{clientSortArrow('lastVisit')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(showAllClients ? clientsView : clientsView.slice(0, 15)).map((c) => (
                            <tr
                              key={c.docId}
                              onClick={() => handleRowNavigate(c.docId)}
                              onKeyDown={(event) => handleRowKeyDown(event, c.docId)}
                              role="button"
                              tabIndex={0}
                              style={{ cursor: 'pointer' }}
                              title="Voir le détail de ce client"
                            >
                              <td style={{ fontWeight: 600 }}>{sanitize(c.displayName)}</td>
                              <td>
                                <span className="pill pill-scan">{isPoints ? c.purchases : c.passages}</span>
                              </td>
                              {!(isMobile || isMid) && isPoints && (
                                <td style={{ whiteSpace: 'nowrap' }}>{formatPts(c.totalSpent)} €</td>
                              )}
                              {!(isMobile || isMid) && (
                                <td style={{ whiteSpace: 'nowrap' }}>
                                  {isPoints ? `${formatPts(c.points)} pts` : `${c.giftsUsed} 🎁`}
                                </td>
                              )}
                              <td className="td-date">
                                {c.lastVisit
                                  ? ((isMobile || isMid) ? formatDDMMYY(c.lastVisit) : formatCompactDateFR(c.lastVisit))
                                  : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="card"><p className="mg-empty">
                        {clientSearch ? 'Aucun client trouvé.' : 'Aucun client pour le moment.'}
                      </p></div>
                    )}

                    {!showAllClients && clientsView.length > 15 && (
                      <div className="mg-more">
                        <button type="button" className="show-more eb-btn eb-btn--secondary" onClick={() => setShowAllClients(true)}>Afficher plus</button>
                      </div>
                    )}
                  </div>
                )}

                {/* Tableau (filtré par caisse) */}
                {viewMode === 'activity' && (
                <>
                <div className="table-fidelity-wrap">
                  {filteredHistory.length > 0 ? (
                    <table className="table-fidelity">
                      <thead>
                        <tr>
                          <th>{fidSettings.Type === 'Fidbypoints' ? 'Points' : 'Scans'}</th>
                          <th>Date</th>
                          <th>Compte</th>
                          <th>Note</th>
                          {!(isMobile || isMid) && <th>Type</th>}
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(showAll ? filteredHistory : filteredHistory.slice(0, 10)).map((row, idx) => {
                          const d = (row?.date instanceof Date && !isNaN(row.date)) ? row.date : new Date(0);
                          const formattedDate = (isMobile || isMid) ? formatDDMMYY(d) : formatCompactDateFR(d);
                          const tenMinutes = 10 * 60 * 1000;
                          const isLastTransaction = idx === 0; // relatif au filtre courant
                          const isRecent = Date.now() - d < tenMinutes;

                          let typeLabel = '';
                          let value = '';
                          let pillClass = 'pill';
                          let rowClassName = '';

                          if (row.type === 'resetstamp') {
                            typeLabel = `${row.giftAtTime || 'Gain'} 🎁`;
                            value = `x${row.count || 1}`;
                            pillClass += ' pill-gift';
                            rowClassName = 'row-gift';
                          } else if (row.type === 'pointshistory') {
                            if (row.points > 0) {
                              typeLabel = 'Achat 🛒';
                              value = `+${formatPts(row.points)} pts`;
                              pillClass += ' pill-pos';
                            } else {
                              typeLabel = `${row.gift || 'Gain'} 🎁`;
                              value = `${formatPts(row.points)} pts`;
                              pillClass += ' pill-gift';
                              rowClassName = 'row-gift';
                            }
                          } else { // timestamp
                            typeLabel = 'Scan 🛒';
                            value = `+${row.count || 1}`;
                            pillClass += ' pill-scan';
                          }

                          let valueCompact = value;
                          if (row.type === 'pointshistory') {
                            const absPts = Math.abs(parseFlexibleNumber(row.points));
                            valueCompact = `${formatPts(absPts)}`;
                          } else if (row.type === 'timestamp' || row.type === 'resetstamp') {
                            valueCompact = String(row.count || 1);
                          }

                          const canDelete = isLastTransaction &&
                            (row.type === 'timestamp' || row.type === 'resetstamp' || row.type === 'pointshistory') &&
                            isRecent;

                          const currentCustomerData = fidelityHistory.find(item => (item.docId || item.clientUid || item.id) === row.customerId);
                          const totalCustomerPoints = currentCustomerData?.points || 0;

                          const accountDisplay =
                            row.uid === user?.uid ? 'Vous' : sanitize(userPseudos[row.uid] || row.uid);

                          return (
                            <tr
                              key={row.id}
                              className={rowClassName}
                              onClick={() => handleRowNavigate(row.docId || row.customerId || row.clientUid)}
                              onKeyDown={(event) => handleRowKeyDown(event, row.docId || row.customerId || row.clientUid)}
                              role="button"
                              tabIndex={0}
                              style={{ cursor: (row.docId || row.customerId || row.clientUid) ? 'pointer' : 'default' }}
                              title={(row.docId || row.customerId || row.clientUid) ? 'Voir le détail de ce client' : undefined}
                            >
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {(isMobile || isMid) ? (
                                  <span className={pillClass}>{valueCompact}</span>
                                ) : (
                                  <span className={pillClass}>
                                    {row.type === 'timestamp' && <FaShoppingCart aria-hidden="true" style={{ marginRight: 6 }} />}
                                    {row.type !== 'timestamp' && row.type !== 'pointshistory' && <FaGift aria-hidden="true" style={{ marginRight: 6 }} />}
                                    {row.type === 'pointshistory' && (row.points > 0 ? <FaShoppingCart aria-hidden="true" style={{ marginRight: 6 }} /> : <FaGift aria-hidden="true" style={{ marginRight: 6 }} />)}
                                    {value}
                                  </span>
                                )}
                              </td>
                              <td className="td-date">{formattedDate}</td>
                              <td>{accountDisplay}</td>
                              <td style={{ textAlign: 'center' }}>
                                {(() => {
                                  const feedback = findFeedbackForRow(row);
                                  const rating = feedback ? (Number(feedback.rating) || 0) : 0;
                                  const comment = typeof feedback?.comment === 'string' ? sanitize(feedback.comment) : '';
                                  if (!rating && !comment) return <span style={{ color: 'var(--eb-subtle)' }}>—</span>;
                                  return (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                      {rating > 0 && (
                                        <>
                                          <FaStar style={{ color: '#F5B301' }} />
                                          <strong style={{ color: 'var(--eb-text)' }}>{rating}</strong>
                                        </>
                                      )}
                                      {comment && (
                                        <button
                                          type="button"
                                          className="comment-badge"
                                          onClick={(event) => { event.stopPropagation(); handleOpenCommentModal(feedback); }}
                                          title="Voir le commentaire"
                                          aria-label="Voir le commentaire"
                                        >
                                          <FaCommentDots />
                                        </button>
                                      )}
                                    </span>
                                  );
                                })()}
                              </td>
                              {!(isMobile || isMid) && (
                                <td className="td-type" title={typeLabel} aria-label={typeLabel}>
                                  <span className="label">{typeLabel}</span>
                                </td>
                              )}
                              <td style={{ textAlign: 'center' }}>
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            
                                  {isLastTransaction && fidSettings.Type === 'Fidbypoints' && row.type === 'pointshistory' && row.points > 0 &&
                                    (pointsPaliers.length > 0 && totalCustomerPoints >= pointsPaliers[0].points) && (
                                      <button
                                        className="button-validation"
                                        style={{ whiteSpace: 'nowrap', minHeight: (isMobile || isMid) ? 36 : 32, padding: (isMobile || isMid) ? '4px 8px' : '4px 10px' }}
                                        onClick={(event) => { event.stopPropagation(); handleOpenGainsModal(row.docId || row.customerId || row.clientUid); }}
                                      >
                                        {(isMobile || isMid) ? '🎟️' : 'Utiliser un gain'}
                                      </button>
                                    )}
                                  {canDelete && (
                                    <button
                                      type="button"
                                      className="mg-icon-btn"
                                      aria-label="Supprimer la dernière transaction"
                                      title="Supprimer"
                                      onClick={(event) => { event.stopPropagation(); handleDeleteLastTransaction(row); }}
                                    >
                                      <FaTrash size={(isMobile || isMid) ? 18 : 15} />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <div className="card"><p className="mg-empty">Aucun historique.</p></div>
                  )}
                </div>

                {!showAll && filteredHistory.length > 10 && (
                  <div className="mg-more">
                    <button type="button" className="show-more eb-btn eb-btn--secondary" onClick={() => setShowAll(true)}>Afficher plus</button>
                  </div>
                )}
                </>
                )}
              </>
            )}

            {/* ──────────────── MODALES (toujours montées dans le DOM de cette branche) ──────────────── */}
            {commentModalData && (
              <div className="modal-overlay mg-modal" onClick={handleCloseCommentModal}>
                <div className="modal-card" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                  <h3>Commentaire client</h3>
                  {commentModalData.rating > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: '8px' }}>
                      <FaStar style={{ color: '#F5B301' }} />
                      <strong>{commentModalData.rating}/5</strong>
                    </div>
                  )}
                  <p style={{ margin: '0 0 12px', whiteSpace: 'pre-wrap', color: 'var(--eb-text-2)', overflowWrap: 'anywhere' }}>{commentModalData.comment}</p>
                  {commentModalData.createdAt && (
                    <div style={{ fontSize: 13, color: 'var(--eb-muted)' }}>
                      {formatCompactDateFR(commentModalData.createdAt)}
                    </div>
                  )}
                  <div className="mg-modal-actions">
                    <button className="eb-btn eb-btn--secondary" onClick={handleCloseCommentModal}>Fermer</button>
                  </div>
                </div>
              </div>
            )}

            {showGainsModal && (
              <div className="modal-overlay mg-modal">
                <div className="modal-card" role="dialog" aria-modal="true">
                  <h3>Utiliser un gain</h3>
                  <div className="mg-modal-list">
                  {currentCustomerGains.length > 0 ? (
                    currentCustomerGains.map((gain, index) => (
                      <button
                        key={index}
                        className="eb-btn eb-btn--success eb-btn--block"
                        onClick={() => handleUseGain(gain)}
                        style={{ whiteSpace: 'normal', lineHeight: 1.3, padding: '10px 16px' }}
                      >
                        {isMobile || isMid ? `🎟️ ${gain.points} pts` : `Utiliser ${gain.points} pts : ${gain.gift}`}
                      </button>
                    ))
                  ) : <p className="eb-muted" style={{ margin: 0 }}>Aucun gain disponible.</p>}
                  </div>
                  <div className="mg-modal-actions">
                    <button className="eb-btn eb-btn--secondary" onClick={() => { setShowGainsModal(false); setCurrentCustomerGains([]); }}>Annuler</button>
                  </div>
                </div>
              </div>
            )}

            {showIncrementModal && (
              <div className="modal-overlay mg-modal">
                <div className="modal-card" role="dialog" aria-modal="true">
                  <h3>Nombre d'articles</h3>
                  <input
                    type="number"
                    min="1"
                    value={tempIncrement}
                    onChange={(e) => setTempIncrement(sanitize(e.target.value))}
                    className="eb-input"
                    inputMode="numeric"
                    style={{ maxWidth: 140, textAlign: 'center', fontSize: 18, fontWeight: 600 }}
                  />
                  <div className="mg-modal-actions">
                    <button className="eb-btn eb-btn--secondary" onClick={() => setShowIncrementModal(false)}>Annuler</button>
                    <button className="eb-btn eb-btn--primary" onClick={handleConfirmIncrement}>Confirmer</button>
                  </div>
                </div>
              </div>
            )}

            {showPointsModal && (
              <div className="modal-overlay mg-modal">
                <div className="modal-card" role="dialog" aria-modal="true">
                  <h3>Ajouter des points</h3>
                  <label htmlFor="amountInput" className="eb-label" style={{ display: 'block', marginBottom: 6 }}>Montant de l'achat :</label>
                  <input
                    id="amountInput"
                    type="number"
                    min="0"
                    step="0.01"
                    value={amountInput}
                    onChange={(e) => setAmountInput(sanitize(e.target.value.replace(',', '.')))}
                    className="eb-input"
                    inputMode="decimal"
                    style={{ maxWidth: 180, fontSize: 18, fontWeight: 600 }}
                  />
                  <div className="mg-modal-actions">
                    <button className="eb-btn eb-btn--secondary" onClick={() => { setShowPointsModal(false); }}>Annuler</button>
                    <button className="eb-btn eb-btn--primary" onClick={handleConfirmPoints}>Confirmer</button>
                  </div>
                </div>
              </div>
            )}
            {/* ──────────────── /MODALES ──────────────── */}
          </>
        )}
        </div>
      </div>
    </div>
  );
};

export default MerchantGestion;
